import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Activity, History, Users, HelpCircle } from 'lucide-react'
import HelpModal from '../../../components/HelpModal'
import { hapticImpact } from '../../../utils/haptic'
import useCrashStore from '../store/useCrashStore'
import { useCrashSocket } from '../hooks/useCrashSocket'
import { useCrashMultiplier } from '../hooks/useCrashMultiplier'
import { useCrashBettingTimer } from '../hooks/useCrashBettingTimer'
import useBreakpoint from '../../../hooks/useBreakpoint'
import { PHASES } from '../config/crashConfig'
import CrashHistoryStrip from '../components/CrashHistoryStrip'
import CrashOscilloscope from '../components/CrashOscilloscope'
import CrashPlayersList from '../components/CrashPlayersList'
import { CrashMyBetWinSkin } from '../components/CrashMyBet'
import CrashActionButton, { CrashBetPanel } from '../components/CrashActionButton'
import CrashOfflineNotice from '../components/CrashOfflineNotice'
import CrashDisabledStub from '../components/CrashDisabledStub'
import TeamCapturePreview from '../../team/components/TeamCapturePreview'
import useUpdateBlocker from '../../../hooks/useUpdateBlocker'

// Рецепт глубины карточек из docs/design-system.md (top-light блик + многослойная тень)
const CARD_DEPTH =
  'shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_10px_15px_-3px_rgba(0,0,0,0.25),0_4px_6px_-4px_rgba(0,0,0,0.25)]'

/**
 * Главный экран режима Краш.
 *
 * Архитектура по краш-документации:
 * - общий сокет приложения (useCrashSocket), на connect повторять subscribe;
 * - множитель считается клиентом в RAF (useCrashMultiplier) + watchdog по тикам;
 * - ставка переживает разрыв — восстанавливается из crash:players;
 * - своя ставка — единая кнопка действия под полем (CrashActionButton) на всех брейкпоинтах;
 * - список игроков — Map по chatId, идемпотентно (CrashPlayersList).
 *
 * Раскладка: mobile-first — поле, кнопка действия (CrashActionButton), своя ставка,
 * статус панели, игроки; форма ставки на mobile открывается шторкой из CrashActionButton.
 * На desktop (lg+) две колонки: слева поле + единая кнопка действия,
 * справа sticky-колонка с формой ставки и списком игроков. Форма на desktop
 * остаётся видимой после ставки, во время роста множителя и после краха;
 * повторно ставить нельзя — доступность отправки определяет фаза раунда.
 */
const Crash = () => {
  useCrashSocket()         // подключение сокета + подписки на события
  useCrashMultiplier()     // RAF-цикл множителя + watchdog
  const navigate = useNavigate()
  const { t } = useTranslation()
  const isDesktop = useBreakpoint('(min-width: 1024px)')

  const phase = useCrashStore((s) => s.phase)
  // Отдельный флаг, а не фаза: текущий раунд доигрывается, заглушка — поверх панели ставки
  const isDisabled = useCrashStore((s) => s.isDisabled)
  const isAuthorized = useCrashStore((s) => s.isAuthorized)
  const isConnected = useCrashStore((s) => s.isConnected)
  const hasSnapshot = useCrashStore((s) => s.hasSnapshot)
  const limits = useCrashStore((s) => s.limits)
  const myBet = useCrashStore((s) => s.myBet)
  const playersCount = useCrashStore((s) => s.players.size)
  const { isExpired } = useCrashBettingTimer()
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  useUpdateBlocker(!isConnected || !hasSnapshot)

  const helpSteps = useMemo(() => ([
    t('crash.help.step1'),
    t('crash.help.step2'),
    t('crash.help.step3'),
    t('crash.help.step4')
  ]), [t])

  // Скролл наверх при монтировании
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }, [])

  const isBetting = phase === PHASES.BETTING
  // Поле ввода блокируем до первого crash:round (нет лимитов — §4)
  const canBet = isBetting && limits && !isExpired && isConnected && isAuthorized

  // «Сырая» подсказка панели по состоянию раунда (ключ локали в crash.notices)
  const rawNotice = canBet
    ? null
    : isBetting && isExpired
      ? 'betClosed'
      : !isBetting
        ? (phase === PHASES.IDLE ? 'connecting' : null)
        : isConnected && !isAuthorized ? 'observer' : 'waitingLimits'
  // Показываем подсказку, только если она продержалась (мгновенные вспышки глушим)
  const panelNotice = useDebounced(rawNotice, 300)
  const showNotice = panelNotice === rawNotice ? panelNotice : null

  return (
    <div className="px-4 py-6 pb-20 lg:pb-8 lg:px-6">
      <div className="mx-auto max-w-7xl">
        {/* Шапка — как в режиме «Кейсы»: зелёный заголовок по центру
            + иконка справки рядом (как в Double). Кнопка истории:
            mobile — в самом низу под списком игроков,
            desktop — внизу правой колонки (как в Double) */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <h1 className="text-2xl font-black italic text-accent-primary uppercase lg:text-4xl flex items-center gap-2">
            <Activity className="w-6 h-6 lg:w-8 lg:h-8" />
            {t('crash.title')}
          </h1>
          <button
            type="button"
            onClick={() => {
              hapticImpact('light')
              setIsHelpOpen(true)
            }}
            className="p-1.5 rounded-full bg-dark-secondary/50 hover:bg-dark-secondary transition-colors active:scale-[0.98]"
            aria-label={t('crash.help.button')}
          >
            <HelpCircle className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        {/* Лента истории */}
        <CrashHistoryStrip />

        {/* Двухколоночная раскладка на desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 lg:gap-6 mt-4">
          {/* Поле игры + своя ставка (desktop: левая колонка, ряд 1) */}
          <div className="space-y-4 lg:col-start-1 lg:row-start-1">
            <div className={`relative rounded-2xl bg-surface border border-white/10 overflow-hidden ${CARD_DEPTH}`}>
              {/* Таймер приёма ставок рисует сам осциллограф крупными секундами
                  по центру поля (пока идёт приём ставок — вместо множителя) */}

              {/* Осциллограф-ЭКГ / крах */}
              <div className="relative h-[340px] lg:h-[440px]">
                <CrashOscilloscope />
              </div>

              {/* Плашка «связь потеряна» */}
              <CrashOfflineNotice />
            </div>

            {/* Единая кнопка действия под полем (mobile + desktop):
                Поставить (mobile) / Ваша ставка / Вывести N / Выведено +N / Проиграно −N */}
            <CrashActionButton />

            {/* Пост-выводные плашки свой ставки: «подбираем предмет» + карточка
                выигранного скина. Состав и сумма ставки видны в своей строке
                списка игроков и на кнопке действия — отдельную карточку не рисуем */}
            <CrashMyBetWinSkin />
          </div>

          {/* Правая колонка (desktop): панель ставки + список игроков.
              На mobile — панель ставки идёт вторым блоком потока, игроки — ниже */}
          <div className="lg:col-start-2 lg:row-start-1 lg:sticky lg:top-4 lg:self-start space-y-4">
            {isDisabled ? (
              <CrashDisabledStub />
            ) : (
              // На mobile статусную карточку не рисуем вовсе: состояние раунда
              // показывает неактивная кнопка CrashActionButton под полем.
              // На desktop форма остаётся видимой после ставки и результата;
              // она не исчезает при обрыве связи или между раундами.
              // Повторная ставка запрещена после принятия своей ставки;
              // отправка зависит от фазы и наличия своей ставки.
              isDesktop && (isAuthorized ? (
                <CrashBetPanel variant="inline" canPlaceBet={Boolean(canBet && !myBet)} />
              ) : (
                <>
                  {/* Переходные подсказки показываем через дебаунс
                      (useDebounced) — иначе на старте раунда видна вспышка
                      «Загрузка лимитов…»/«Приём ставок закрыт» на долю секунды,
                      пока не прилетели limits/дедлайн от сервера */}
                  {!canBet && showNotice && (
                    <div className={`rounded-2xl bg-surface border border-white/10 p-4 text-center py-8 text-text-secondary text-sm ${CARD_DEPTH}`}>
                      {t(`crash.notices.${showNotice}`)}
                    </div>
                  )}
                </>
              ))
            )}

            {/* Игроки — только desktop (узкая правая колонка, на всю ширину не разъезжаются) */}
            {playersCount > 0 && (
              <div className={`hidden lg:block rounded-2xl bg-surface border border-white/10 p-3 ${CARD_DEPTH}`}>
                <div className="flex items-center gap-1.5 mb-3 text-sm font-semibold text-text-secondary">
                  <Users className="w-4 h-4" />
                  <span className="tabular-nums">{playersCount}</span>
                </div>
                <div className="lg:max-h-[480px] lg:overflow-y-auto lg:pr-1">
                  <CrashPlayersList />
                </div>
              </div>
            )}

            {/* История — desktop, внизу правой колонки (стиль как в Double) */}
            <div className="hidden lg:flex justify-center">
              <button
                onClick={() => {
                  hapticImpact('light')
                  navigate('/crash/history')
                }}
                className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-full bg-surface text-text-secondary hover:text-white border border-white/10 transition-colors active:scale-[0.98]"
              >
                <History className="w-4 h-4" />
                <span className="text-sm">{t('crash.buttons.history')}</span>
              </button>
            </div>
          </div>

          {/* Игроки — mobile, после панели ставки.
              Без игроков карточку не рисуем — пустой блок не нужен */}
          {playersCount > 0 && (
            <div className={`lg:hidden rounded-2xl bg-surface border border-white/10 p-3 ${CARD_DEPTH}`}>
              <div className="flex items-center gap-1.5 mb-3 text-sm font-semibold text-text-secondary">
                <Users className="w-4 h-4" />
                <span className="tabular-nums">{playersCount}</span>
              </div>
              <CrashPlayersList />
            </div>
          )}

          {/* История — mobile, в самом низу под чужими ставками (стиль как в Double) */}
          <div className="lg:hidden flex justify-center">
            <button
              onClick={() => {
                hapticImpact('light')
                navigate('/crash/history')
              }}
              className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-full bg-surface text-text-secondary hover:text-white border border-white/10 transition-colors active:scale-[0.98]"
            >
              <History className="w-4 h-4" />
              <span className="text-sm">{t('crash.buttons.history')}</span>
            </button>
          </div>
        </div>
        <TeamCapturePreview mode="crash" className="mx-auto mt-8 max-w-3xl" />
      </div>

      <HelpModal
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title={t('crash.help.title')}
        steps={helpSteps}
        highlightLastStep={false}
      />
    </div>
  )
}

export default Crash

/**
 * Дебаунс значения: возвращает последнее значение, которое продержалось
 * `ms` миллисекунд. Нужен, чтобы переходные подсказки панели ставки
 * не мигали на долю секунды при смене фаз/прилёте лимитов.
 */
function useDebounced(value, ms = 300) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return debounced
}
